import CustomReusableModal from "@bevco/Components/CustomReusableModal";
import cancelQuote from "@bevco/Store/Handlers/CustomModals/CancelQuote";
import editQuote from "@bevco/Store/Handlers/CustomModals/EditQuote";
import placeOrder from "@bevco/Store/Handlers/CustomModals/PlaceOrder";
import requote from "@bevco/Store/Handlers/CustomModals/Requote";
import showHideCustomReusableModal from "@bevco/Store/Handlers/CustomModals/ShowHideCustomReusableModal";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import acceptJobQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/AcceptJobQuote";
import declineQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeclineQuote";
import deleteQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeleteQuote";
import submitQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SubmitQuote";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code - Start */
import { parseOrderType } from "@bevco/Utilities/Utilities";
/* Valtech Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
    checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    rfqJobQuotesPageUrl: getPageLinkByPageType(state, "RfqJobQuotesPage")?.url,
    cartState: getCurrentCartState(state),
    hideActionButtons: (state.context.settings.settingsCollection as any).myAccountSettings?.hideActionButtons,
    onePageCheckout: getSettingsCollection(state).cartSettings.onePageCheckout,
    onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
});

const mapDispatchToProps = {
    deleteQuote,
    submitQuote,
    declineQuote,
    acceptJobQuote,

    /* Verndale Custom Feature - New methods */
    showHideCustomReusableModal,
    placeOrder,
    requote,
    editQuote,
    cancelQuote,
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsHeaderStyles {
    headerGridContainer?: GridContainerProps;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    deleteQuoteButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    declineQuoteButton?: ButtonPresentationProps;
    acceptQuoteButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    deleteQuoteClickable?: ClickableProps;
    submitQuoteClickable?: ClickableProps;
    declineQuoteClickable?: ClickableProps;
    acceptQuoteClickable?: ClickableProps;
    deleteQuoteModal?: TwoButtonModalStyles;
    cartNotificationModal?: TwoButtonModalStyles;

    /* Verndale Custom Feature - New styles */
    placeOrderButton?: ButtonPresentationProps;
    placeOrderClickable?: ClickableProps;
    requoteButton?: ButtonPresentationProps;
    requoteClickable?: ClickableProps;
    editQuoteButton?: ButtonPresentationProps;
    editQuoteClickable?: ClickableProps;
    cancelQuoteButton?: ButtonPresentationProps;
    cancelQuoteClickable?: ClickableProps;
}

export const rfqQuoteDetailsHeaderStyles: RfqQuoteDetailsHeaderStyles = {
    headerGridContainer: {
        gap: 10,
    },
    deleteQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    submitQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    declineQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    acceptQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [2, 2, 2, 7, 6],
    },
    titleGridItem: {
        width: [10, 10, 10, 5, 6],
    },
    title: {
        variant: "h3",
        as: "h1",
        css: css`
            @media print {
                font-size: 11px;
            }
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = rfqQuoteDetailsHeaderStyles;

const RfqQuoteDetailsHeader: FC<Props> = ({
    quoteState,
    history,
    rfqMyQuotesPageUrl,
    checkoutShippingPageUrl,
    rfqJobQuotesPageUrl,
    cartState,
    onePageCheckout,
    onePageCheckoutPageUrl, 
    deleteQuote,
    submitQuote,
    declineQuote,
    acceptJobQuote,

    /* Verndale Custom Feature - New methods */
    showHideCustomReusableModal,
    placeOrder,
    requote,
    editQuote,
    cancelQuote,
    hideActionButtons,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [deleteQuoteModalIsOpen, setDeleteQuoteModalIsOpen] = useState(false);
    const [cartNotificationModalIsOpen, setCartNotificationModalIsOpen] = useState(false);

    if (!quoteState.value) {
        return null;
    }

    const quote = quoteState.value;

    /* Valtech Custom Code - Start */
    const { status } = quote;
    const { orderChannel, itemsTotal } = quote?.properties || {};
    const orderType = parseOrderType(orderChannel, true);
    const quantity = quote.quoteLineCollection?.length || 0;
    const value = Number(itemsTotal);
    /* Valtech Custom Code - End */

    const deleteQuoteClickHandler = () => {
        setDeleteQuoteModalIsOpen(true);
    };

    const submitDeleteQuoteHandler = () => {
        deleteQuote({
            quoteId: quote.id,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quote deleted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                this.onSuccess?.();
            },
        });
    };

    const cancelDeleteQuoteHandler = () => {
        setDeleteQuoteModalIsOpen(false);
    };

    const submitCartNotificationHandler = () => {
        redirectToCheckout();
    };

    const cancelCartNotificationHandler = () => {
        setCartNotificationModalIsOpen(false);
    };

    const submitQuoteClickHandler = () => {
        submitQuote({
            quote,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quote submitted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    const declineQuoteClickHandler = () => {
        declineQuote({
            quote,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quote declined."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    const acceptQuoteClickHandler = () => {
        if (quote.isJobQuote) {
            acceptJobQuote({
                quote,
                onSuccess: () => {
                    toasterContext.addToast({ body: translate("Quote accepted."), messageType: "success" });
                    redirectToJobQuotes();
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.();
                    }
                },
            });
        } else if (cartState.value?.lineCount === 0) {
            redirectToCheckout();
        } else {
            setCartNotificationModalIsOpen(true);
        }
    };

    const redirectToMyQuotes = () => {
        if (rfqMyQuotesPageUrl) {
            history.push(rfqMyQuotesPageUrl);
        }
    };

    const redirectToCheckout = () => {
		if (onePageCheckout && onePageCheckoutPageUrl) {
            history.push(`${onePageCheckoutPageUrl}?cartId=${quote.id}`);
            return;
        }

        if (checkoutShippingPageUrl) {
            history.push(`${checkoutShippingPageUrl}?cartId=${quote.id}`);
        }
    };

    const redirectToJobQuotes = () => {
        if (rfqJobQuotesPageUrl) {
            history.push(rfqJobQuotesPageUrl);
        }
    };

    const expirationDateIsGreaterThanCurrentDate = () => {
        if (!quote || !quote.expirationDate) {
            return true;
        }

        const expirationDate = new Date(quote.expirationDate.toString());
        const currentDate = new Date();

        expirationDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        return expirationDate >= currentDate;
    };

    const quoteType = quote.isJobQuote ? "Job Quote" : "Sales Quote";
    const printLabel = translate("Print");
    const deleteQuoteLabel = translate(`Delete ${quoteType}`);
    const submitQuoteLabel = translate(`Submit ${quoteType}`);
    const declineQuoteLabel = translate(`Decline ${quoteType}`);
    const acceptQuoteLabel = translate(`Accept ${quoteType}`);
    const canBeSubmittedOrDeleted = quote.isSalesperson && quote.isEditable;
    const expirationDateIsValid = expirationDateIsGreaterThanCurrentDate();
    const canBeDeclined = !quote.isSalesperson && quote.status === "QuoteProposed" && expirationDateIsValid;
    const canBeAccepted =
        !quote.failedToGetRealTimeInventory &&
        !quote.isSalesperson &&
        (quote.status === "QuoteProposed" || quote.status === "AwaitingApproval") &&
        expirationDateIsValid;

    const handleNewActions = (key: string) => {
        showHideCustomReusableModal({
            orderNumber: quote.id,
            key,
            show: true,
        });
    };

    return (
        <>
            <GridContainer {...styles.headerGridContainer}>
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.title}>{`${translate('Quotation #')} ${quote.quoteNumber || 'Pending'}`}</Typography>
                </GridItem>
                <GridItem {...styles.buttonGridItem}>
                    <Hidden {...styles.menuHiddenContainer}>
                        <OverflowMenu {...styles.narrowOverflowMenu}>
                            {/* Verndale Custom Feature - New Action */}
                            {quote.status === "QuoteCompleted" && !hideActionButtons && (
                                <Clickable
                                    {...styles.placeOrderClickable}
                                    onClick={() => handleNewActions("place-order")}
                                >
                                    {translate("Place Order")}
                                </Clickable>
                            )}

                            {/* Verndale Custom Feature - New Action */}
                            {quote.status === "QuoteExpired" && !hideActionButtons && (
                                <Clickable {...styles.requoteClickable} onClick={() => handleNewActions("requote")}>
                                    {translate("Requote")}
                                </Clickable>
                            )}

                            {/* Verndale Custom Feature - New Action */}
                            {quote.status === "QuoteOpen" && !hideActionButtons && (
                                <>
                                    <Clickable
                                        {...styles.editQuoteClickable}
                                        onClick={() => handleNewActions("edit-quote")}
                                    >
                                        {translate("Edit Quote")}
                                    </Clickable>
                                    <Clickable
                                        {...styles.cancelQuoteClickable}
                                        onClick={() => handleNewActions("cancel-quote")}
                                    >
                                        {translate("Cancel Quote")}
                                    </Clickable>
                                </>
                            )}

                            <Clickable {...styles.printClickable} onClick={openPrintDialog}>
                                {printLabel}
                            </Clickable>
                            {canBeSubmittedOrDeleted && (
                                <>
                                    <Clickable {...styles.deleteQuoteClickable} onClick={deleteQuoteClickHandler}>
                                        {deleteQuoteLabel}
                                    </Clickable>
                                    <Clickable {...styles.submitQuoteClickable} onClick={submitQuoteClickHandler}>
                                        {submitQuoteLabel}
                                    </Clickable>
                                </>
                            )}
                            {canBeDeclined && (
                                <Clickable {...styles.declineQuoteClickable} onClick={declineQuoteClickHandler}>
                                    {declineQuoteLabel}
                                </Clickable>
                            )}
                            {canBeAccepted && (
                                <Clickable {...styles.acceptQuoteClickable} onClick={acceptQuoteClickHandler}>
                                    {acceptQuoteLabel}
                                </Clickable>
                            )}
                        </OverflowMenu>
                    </Hidden>
                    <Hidden {...styles.buttonsHiddenContainer}>
                        {/* Verndale Custom Feature - New Action */}
                        {quote.status === "QuoteCompleted" && !hideActionButtons && (
                            <Button {...styles.placeOrderButton} onClick={() => handleNewActions("place-order")}>
                                {translate("Place Order")}
                            </Button>
                        )}

                        {/* Verndale Custom Feature - New Action */}
                        {quote.status === "QuoteExpired" && !hideActionButtons && (
                            <Button {...styles.requoteButton} onClick={() => handleNewActions("requote")}>
                                {translate("Requote")}
                            </Button>
                        )}

                        {/* Verndale Custom Feature - New Action */}
                        {quote.status === "QuoteOpen" && !hideActionButtons && (
                            <>
                                <Button {...styles.editQuoteButton} onClick={() => handleNewActions("edit-quote")}>
                                    {translate("Edit Quote")}
                                </Button>
                                <Button {...styles.cancelQuoteButton} onClick={() => handleNewActions("cancel-quote")}>
                                    {translate("Cancel Quote")}
                                </Button>
                            </>
                        )}

                        <Button {...styles.printButton} onClick={openPrintDialog}>
                            {printLabel}
                        </Button>
                        {canBeSubmittedOrDeleted && (
                            <>
                                <Button
                                    {...styles.deleteQuoteButton}
                                    onClick={deleteQuoteClickHandler}
                                    data-test-selector="rfqQuoteDetails_deleteQuote"
                                >
                                    {deleteQuoteLabel}
                                </Button>
                                <Button
                                    {...styles.submitQuoteButton}
                                    onClick={submitQuoteClickHandler}
                                    data-test-selector="rfqQuoteDetails_submitQuote"
                                >
                                    {submitQuoteLabel}
                                </Button>
                            </>
                        )}
                        {canBeDeclined && (
                            <Button
                                {...styles.declineQuoteButton}
                                onClick={declineQuoteClickHandler}
                                data-test-selector="rfqQuoteDetails_declineQuote"
                            >
                                {declineQuoteLabel}
                            </Button>
                        )}
                        {canBeAccepted && (
                            <Button
                                {...styles.acceptQuoteButton}
                                onClick={acceptQuoteClickHandler}
                                data-test-selector="rfqQuoteDetails_acceptQuote"
                            >
                                {acceptQuoteLabel}
                            </Button>
                        )}
                    </Hidden>
                </GridItem>
            </GridContainer>
            <TwoButtonModal
                {...styles.deleteQuoteModal}
                modalIsOpen={deleteQuoteModalIsOpen}
                headlineText={translate("Delete Quote")}
                messageText={siteMessage("Rfq_DeleteQuoteConfirmation")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={cancelDeleteQuoteHandler}
                onSubmit={submitDeleteQuoteHandler}
                submitTestSelector="submitDeleteQuote"
            />
            <TwoButtonModal
                {...styles.cartNotificationModal}
                modalIsOpen={cartNotificationModalIsOpen}
                headlineText={translate("Cart Notification")}
                messageText={siteMessage("Rfq_CartNotificationMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={cancelCartNotificationHandler}
                onSubmit={submitCartNotificationHandler}
                submitTestSelector="submitCartNotification"
            />

            <CustomReusableModal
                id="place-order"
                headline={translate("Place Order")}
                subHeadline={translate(
                    `Please provide a PO number and confirm your shipping address below. Click "YES" to confirm your order request.`,
                )}
                messageHint={translate("Enter additional information about your request")}
                submitLabel={translate("Yes")}
                cancelLabel={translate("No")}
                errorMessage={siteMessage("Change_Message_Error") as string}
                submitRequest={placeOrder}
                // Valtech Custom Code
                trackingDetails={{trackingKey: "quote_place_order", status, orderType, value, quantity}}
            ></CustomReusableModal>

            <CustomReusableModal
                id="requote"
                headline={translate("Requote")}
                subHeadline={translate(
                    "Are you sure you would like a sales rep to re-quote all associated products and selections?",
                )}
                messageHint={translate("Enter additional information about your request")}
                submitLabel={translate("Yes")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Requote_Message_Error") as string}
                submitRequest={requote}
                // Valtech Custom Code
                trackingDetails={{trackingKey: "requote_quote", status, orderType, value, quantity}}
            ></CustomReusableModal>

            <CustomReusableModal
                id="edit-quote"
                headline={translate("Edit Quote")}
                subHeadline={translate("Why would you like to change this quote?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter changes needed to your quote")}
                submitLabel={translate("Request Change")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Edit_Message_Error") as string}
                submitRequest={editQuote}
                // Valtech Custom Code
                trackingDetails={{trackingKey: "edit_quote", status, orderType, value, quantity}}
            ></CustomReusableModal>

            <CustomReusableModal
                id="cancel-quote"
                headline={translate("Cancel Quote")}
                subHeadline={translate("Why would you like to cancel this quote?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter additional information about quote cancelation")}
                submitLabel={translate("Request Cancelation")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Cancel_Message_Error") as string}
                submitRequest={cancelQuote}
                // Valtech Custom Code
                trackingDetails={{trackingKey: "cancel_quote", status, orderType, value, quantity}}
            ></CustomReusableModal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqQuoteDetailsHeader) as any) as any,
    definition: {
        displayName: "Page Header",
        allowedContexts: ["RfqQuoteDetailsPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
